import { Card, Flex, Space } from 'antd';
import { createContext, useContext, type PropsWithChildren, type ReactNode } from 'react';

/**
 * 嵌进别人的页签里时，子页不再画自己的页头（外层已有页面标题），只保留右上角操作区。
 * 由日志管理合并页这类「一页多页签、正文仍是原页面」的地方提供。
 */
export const EmbeddedPageContext = createContext(false);

interface CommonPageProps {
  /** 页面标题，通常是当前菜单名 */
  title?: ReactNode;
  /** 右上角操作区 */
  extra?: ReactNode;
  /** 是否用 Card 包裹内容 */
  card?: boolean;
  /**
   * 内容区撑满可视高度（列表页用）：页面本身不滚动，滚动交给内部的表格
   * 内容型页面（首页、资源管理等）保持 false，让内容区自然增高并滚动
   */
  fullHeight?: boolean;
}

/**
 * 通用页面容器（对应 vue-naive-admin 的 CommonPage）
 * 标题 + 右侧操作区 + 内容卡片
 */
export default function CommonPage({
  title,
  extra,
  card = true,
  fullHeight = false,
  children,
}: PropsWithChildren<CommonPageProps>) {
  const embedded = useContext(EmbeddedPageContext);
  const content = card ? (
    <Card variant="borderless">{children}</Card>
  ) : (
    children
  );

  // 合并页的页签里：不画页头（标题与页签重复），操作区仍要给
  if (embedded) {
    return (
      <Flex vertical gap={16}>
        {extra && (
          <Flex wrap gap={12} align="center" justify="flex-end">
            <Space wrap className="max-w-full">
              {extra}
            </Space>
          </Flex>
        )}
        {content}
      </Flex>
    );
  }

  return (
    <Flex
      vertical
      gap={16}
      className={fullHeight ? 'admin-page-stack page-full-height h-full' : 'admin-page-stack'}
    >
      {(title || extra) && (
        <Flex wrap gap={12} align="center" justify="space-between" className="min-h-8">
          <span className="page-title">{title}</span>
          <Space wrap className="max-w-full">{extra}</Space>
        </Flex>
      )}
      {fullHeight ? (
        <div className="admin-page-stack flex min-h-0 flex-1 flex-col gap-4">{content}</div>
      ) : (
        content
      )}
    </Flex>
  );
}
